import { useEffect, useState } from 'react'
import {
  useLazyGetUserByEmailQuery,
  useLazyGetUserByIdQuery,
  useLazyGetUserFeaturesQuery,
  useLoginMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} from '../../store/auth/auth.api'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { authSchema, resetPasswordSchema } from './auth.schema'
import { LoginPayload } from '../../store/auth/auth.types'
import { useDispatch } from 'react-redux'
import { setUser, setUserCompanyFeatures } from 'store/auth/auth.slice'
import { featuresResponseToByCompany } from 'utils/features'

export const useAuth = () => {
  const [login, { isLoading: isLoginLoading }] = useLoginMutation()
  const [registerUser, { isLoading: isRegisterLoading }] = useRegisterMutation()
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const [getUserById] = useLazyGetUserByIdQuery()
  const [getUserFeatures] = useLazyGetUserFeaturesQuery()
  const [forgotPassword] = useForgotPasswordMutation()
  const dispatch = useDispatch()

  const [loginError, setLoginError] = useState<string>('')
  const [loginErrorOpen, setLoginErrorOpen] = useState(false)
  const [registerError, setRegisterError] = useState<string>('')
  const [registerErrorOpen, setRegisterErrorOpen] = useState(false)

  const navigate = useNavigate()

  const handleLogin = async ({ username, password }: LoginPayload) => {
    try {
      const data = await login({ username, password }).unwrap()

      // Set session storage for access and refesh tokens
      window.sessionStorage.setItem('accessToken', data?.accessToken)
      !!data?.refreshToken &&
        window.sessionStorage.setItem('refreshToken', data?.refreshToken)

      // Store effective user id (for dispatch users this is adminId)
      if (data?.userId) {
        window.sessionStorage.setItem('userId', data.userId)
      } else {
        // Fallback to fetching user by email for older tokens without userId
        const user = await getUserByEmail({
          email: username,
        })
        if (!!user?.data) window.sessionStorage.setItem('userId', user.data.id)
      }

      // Store role for role-based access control
      if (data?.role) {
        window.sessionStorage.setItem('role', data.role)
      }

      const effectiveUserId = data?.userId ?? window.sessionStorage.getItem('userId')
      if (effectiveUserId) {
        try {
          const user = await getUserById({ userId: effectiveUserId }).unwrap()
          dispatch(setUser({ user }))
        } catch (_error) {
          // Top bar falls back to "Transpots" until AuthUserSync retries
        }

        try {
          const features = await getUserFeatures({ userId: effectiveUserId }).unwrap()
          dispatch(
            setUserCompanyFeatures({
              featuresByCompany: featuresResponseToByCompany(features),
            })
          )
        } catch (_error) {
          dispatch(setUserCompanyFeatures({ featuresByCompany: {} }))
        }
      }

      navigate('/home')
    } catch (error: any) {
      console.log(error)

      if (error?.status === 401) {
        setLoginErrorOpen(true)
        setLoginError('Please enter valid email address and password')
      } else {
        setLoginErrorOpen(true)
        setLoginError(error?.error)
      }
    }
  }

  const handleSignup = async ({ username, password }: LoginPayload) => {
    try {
      await registerUser({
        firstName: '',
        lastName: '',
        email: username,
        password,
        address: '',
      }).unwrap()

      return { success: true as const, email: username }
    } catch (error: any) {
      console.log(error)

      if (error?.status === 401) {
        setRegisterErrorOpen(true)
        setRegisterError('Please enter valid email address and password')
      } else if (error?.status === 500) {
        setRegisterErrorOpen(true)
        setRegisterError(error?.data)
      } else {
        setRegisterErrorOpen(true)
        setLoginError(error?.error)
      }
      return { success: false as const }
    }
  }

  const handleForgotPassword = async (email: string) => {
    try {
      const result = await forgotPassword({ email }).unwrap()
      return { success: true, message: result.message }
    } catch (error: any) {
      console.log(error)
      return {
        success: false,
        message: error?.data?.error || error?.error || 'Failed to send reset email',
      }
    }
  }

  useEffect(() => {
    if (!!window.sessionStorage.getItem('accessToken')) {
      navigate('/home')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    handleLogin,
    handleSignup,
    handleForgotPassword,
    isLoginLoading,
    isRegisterLoading,
    loginError,
    registerError,
    loginErrorOpen,
    setLoginErrorOpen,
    registerErrorOpen,
    setRegisterErrorOpen,
  }
}

export const useLoginForm = () => {
  return useForm({
    resolver: yupResolver(authSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })
}

export const useResetPassword = () => {
  const [resetPassword] = useResetPasswordMutation()
  const [resetError, setResetError] = useState<string>('')
  const [resetErrorOpen, setResetErrorOpen] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const navigate = useNavigate()

  const handleResetPassword = async (
    userId: string,
    code: string,
    password: string
  ) => {
    try {
      const result = await resetPassword({ userId, code, password }).unwrap()
      setResetSuccess(true)
      return { success: true, message: result.message }
    } catch (error: any) {
      console.log(error)
      const errorMessage =
        error?.data?.error || error?.error || 'Failed to reset password'
      setResetError(errorMessage)
      setResetErrorOpen(true)
      return { success: false, message: errorMessage }
    }
  }

  return {
    handleResetPassword,
    resetError,
    resetErrorOpen,
    setResetErrorOpen,
    resetSuccess,
    setResetSuccess,
    navigate,
  }
}

export const useResetPasswordForm = () => {
  return useForm({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })
}
